/**
 * Archive on a project's Home (DashboardView): the "⋯" menu's Archive project… / Unarchive (its owner's; HomeStrip
 * `stripProps`), the confirmation (ArchiveDialog: archived, then Stop all), and the "Archived" banner shown while it is
 * archived (opened by its URL: archived projects are in no list). `archived` is from `.mia/project.json`
 * (`metadata.archived`) until the person changes it here.
 */
import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import ArchiveDialog from './ArchiveDialog';
import { ArchivedBanner } from './ArchivedProjects';
import { unarchiveProject } from '../../services/ProjectArchiveService';

export default function useProjectArchive({ projectId, name, metadata, isOwner, enabled = true, onChanged = null }) {
  const [changed, setChanged] = useState(null); // { archived, archivedAt } after a change here
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setChanged(null); setConfirming(false); }, [projectId]);

  const archived = changed ? changed.archived : metadata?.archived === true;
  const archivedAt = changed ? changed.archivedAt : metadata?.archivedAt || null;
  const settle = (answer) => {
    setChanged({ archived: Boolean(answer?.archived), archivedAt: answer?.archivedAt || null });
    onChanged?.(answer);
  };
  const unarchive = async() => {
    setBusy(true);
    try {
      settle(await unarchiveProject(projectId));
      message.success('Unarchived: it is back in your projects');
    } catch (error) {
      message.error(error.message || 'The project could not be unarchived');
    } finally {
      setBusy(false);
    }
  };
  const on = Boolean(enabled && projectId);
  return {
    archived: on && archived,
    stripProps: on && isOwner ? { archived, onArchive: () => setConfirming(true), onUnarchive: unarchive } : {},
    banner: on && archived ? <ArchivedBanner archivedAt={archivedAt} busy={busy} canUnarchive={Boolean(isOwner)} onUnarchive={unarchive} /> : null,
    dialog: on ? (
      <ArchiveDialog
        onCancel={() => setConfirming(false)}
        onDone={({ archived: done, failed }) => {
          setConfirming(false);
          if (done.includes(projectId)) {
            settle({ archived: true, archivedAt: new Date().toISOString() });
            message.success('Archived: it is hidden from the lists; find it under Projects › Archived');
          }
          if (failed.length) { message.error(failed[0].error); }
        }}
        projects={confirming ? [{ id: projectId, name: name || projectId }] : null}
      />
    ) : null,
  };
}
